import { Controller } from '@hotwired/stimulus'

/**
 * Lead Form Controller (paid landing pages)
 *
 * - Replays UTM/gclid attribution from sessionStorage into hidden inputs, so a
 *   visitor who scrolls to the second form (or refreshes) is still attributed
 *   to the click that paid for them.
 * - Validates, submits as JSON, and swaps in a thank-you panel.
 * - Fires the Google Ads conversion on success.
 *
 * Usage:
 *   <div data-controller="public--lead-form"
 *        data-public--lead-form-instance-value="hero">
 */
const STORAGE_KEY = 'madely_lp_attribution'
const ATTRIBUTION_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid'
]

export default class extends Controller {
  static targets = ['form', 'field', 'attribution', 'submitButton', 'formError', 'success']
  static values = {
    instance: { type: String, default: 'hero' },
    submittingText: { type: String, default: 'Sending…' }
  }

  connect() {
    this.originalButtonText = this.submitButtonTarget.value
    this.captureAttribution()
    this.fillAttribution()

    // Track form-start once per controller instance: bind on each field's
    // `focus` (does not bubble, hence per-field listeners rather than one
    // delegated handler) and let trackFormStart's own flag stop a visitor
    // tabbing between fields from emitting it more than once.
    this.trackFormStart = this.trackFormStart.bind(this)
    this.fieldTargets.forEach((field) => field.addEventListener('focus', this.trackFormStart))
  }

  disconnect() {
    this.fieldTargets.forEach((field) => field.removeEventListener('focus', this.trackFormStart))
  }

  trackFormStart() {
    if (this.formStarted) return
    this.formStarted = true

    if (typeof ahoy !== 'undefined') {
      ahoy.track('lp_form_started', {
        page: window.location.pathname,
        form_instance: this.instanceValue
      })
    }
  }

  // Persist attribution on first load; the query string is gone after any
  // client-side navigation, but the paid click still deserves the credit.
  captureAttribution() {
    const query = new URLSearchParams(window.location.search)
    const fromUrl = {}
    ATTRIBUTION_KEYS.forEach((key) => {
      const value = query.get(key)
      if (value) fromUrl[key] = value
    })

    if (Object.keys(fromUrl).length === 0) return

    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(fromUrl))
    } catch (e) {
      // Private mode / storage disabled — fall back to the live query string.
    }
  }

  storedAttribution() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY)) || {}
    } catch (e) {
      return {}
    }
  }

  fillAttribution() {
    const query = new URLSearchParams(window.location.search)
    const stored = this.storedAttribution()

    this.attributionTargets.forEach((input) => {
      const key = input.name
      input.value = stored[key] || query.get(key) || ''
    })
  }

  async submit(event) {
    event.preventDefault()

    if (!this.validate()) return

    this.setSubmitting(true)
    this.clearFormError()

    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken()
        },
        body: JSON.stringify(this.payload())
      })

      const data = await response.json()

      if (response.ok && data.success) {
        this.onSuccess(data.recorded)
      } else {
        this.showFormError((data.errors || ['Something went wrong. Please try again.']).join('. '))
        this.setSubmitting(false)
      }
    } catch (e) {
      this.showFormError('We could not reach the server. Please try again.')
      this.setSubmitting(false)
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }

  payload() {
    const data = new FormData(this.formTarget)
    const body = { marketing_lead: {} }

    for (const [name, value] of data.entries()) {
      const nested = name.match(/^marketing_lead\[(\w+)\]$/)
      if (nested) {
        body.marketing_lead[nested[1]] = value
      } else {
        body[name] = value
      }
    }

    return body
  }

  validate() {
    let valid = true

    this.fieldTargets.forEach((field) => {
      if (field.checkValidity()) {
        this.clearFieldError(field)
      } else {
        this.showFieldError(field)
        valid = false
      }
    })

    if (!valid) {
      this.fieldTargets.find((f) => !f.checkValidity())?.focus()
    }

    return valid
  }

  showFieldError(field) {
    field.classList.add('is-invalid')
    let error = field.parentElement.querySelector('.c-lp-field-error')
    if (!error) {
      error = document.createElement('span')
      error.className = 'c-lp-field-error'
      error.id = `${field.id}-error`
      error.setAttribute('role', 'alert')
      field.parentElement.appendChild(error)
    }
    error.textContent = field.validity.typeMismatch
      ? 'Please enter a valid email address'
      : 'This field is required'

    field.setAttribute('aria-invalid', 'true')
    field.setAttribute('aria-describedby', error.id)
  }

  clearFieldError(field) {
    field.classList.remove('is-invalid')
    field.parentElement.querySelector('.c-lp-field-error')?.remove()
    field.removeAttribute('aria-invalid')
    field.removeAttribute('aria-describedby')
  }

  showFormError(message) {
    this.formErrorTarget.textContent = message
    this.formErrorTarget.hidden = false
  }

  clearFormError() {
    this.formErrorTarget.textContent = ''
    this.formErrorTarget.hidden = true
  }

  setSubmitting(isSubmitting) {
    this.submitButtonTarget.disabled = isSubmitting
    this.submitButtonTarget.value = isSubmitting
      ? this.submittingTextValue
      : this.originalButtonText
  }

  // `recorded` is false when the operation silently bot-filtered this
  // submission (same JSON success shape either way, see
  // Public::LandingPagesController#respond_lead_success). The visitor still
  // sees the thank-you panel — telling them otherwise would tip off a bot —
  // but the Google Ads conversion pixel must not fire for a lead that was
  // never actually recorded.
  onSuccess(recorded) {
    this.formTarget.hidden = true
    this.successTarget.hidden = false
    this.successTarget.focus?.()

    if (recorded) this.trackConversion()
  }

  trackConversion() {
    if (typeof gtag === 'function' && window.MADELY_ADS_CONVERSION) {
      gtag('event', 'conversion', { send_to: window.MADELY_ADS_CONVERSION })
    }

    if (typeof ahoy !== 'undefined') {
      ahoy.track('ppc_lead_submitted_client', {
        page: window.location.pathname,
        form_instance: this.instanceValue
      })
    }
  }
}
